import { motion } from "framer-motion";
import { BookOpen, Radio } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PRICE } from "@/config";

const EBOOK_IMG = "/book-cover.png";
const WEBINAR_IMG = "/toriano-purple-suit.webp";

const CARDS = [
    {
        num: "01",
        icon: BookOpen,
        title: "The Book",
        subtitle: "Consciousness of Abundance",
        body: "Instant digital download. Learn about mindset, opportunity, business, discipline, branding, abundance, and entrepreneurship.",
        img: EBOOK_IMG,
        testid: "card-book",
    },
    {
        num: "02",
        icon: Radio,
        title: "Live Tuesday Webinar",
        subtitle: "Consciousness of Abundance LIVE",
        body: "Every Tuesday 7 PM Central Time. Learn directly from Toriano about AI, marketing, business, branding, sales, and entrepreneurship.",
        img: WEBINAR_IMG,
        testid: "card-webinar",
    },
];

export const WhatYouGet = () => (
    <section id="what-you-get" data-testid="what-you-get-section" className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80vw] h-[40vw] rounded-full bg-royal/15 blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
            <motion.p
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}
                className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-gold mb-6"
            >
                What You Get
            </motion.p>
            <motion.h2
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.9, delay: 0.1 }}
                className="font-serif uppercase font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mb-14 sm:mb-24"
            >
                Two keys. <span className="violet-text italic">One decision.</span>
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                {CARDS.map((card, i) => (
                    <motion.article
                        key={card.num}
                        data-testid={card.testid}
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                        whileHover={{ y: -8 }}
                        className="group gold-border-beam relative bg-[#120A24] overflow-hidden hover:shadow-[0_24px_90px_rgba(139,92,246,0.3)] transition-shadow duration-500"
                    >
                        <div className="relative h-56 sm:h-64 overflow-hidden">
                            <img src={card.img} alt={card.subtitle} className="w-full h-full object-cover object-top opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-all duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#120A24] via-royal/10 to-transparent" />
                            <span className="absolute top-4 left-6 font-serif italic text-6xl sm:text-7xl gold-text opacity-90">{card.num}</span>
                        </div>
                        <div className="p-6 sm:p-10">
                            <div className="flex items-center gap-3 mb-4">
                                <card.icon className="w-4 h-4 text-orchid" />
                                <h3 className="font-serif uppercase tracking-wide text-2xl sm:text-3xl font-semibold">{card.title}</h3>
                            </div>
                            <p className="font-serif italic text-gold/90 text-lg mb-4">{card.subtitle}</p>
                            <p className="text-sm sm:text-base text-zinc-400 leading-relaxed">{card.body}</p>
                        </div>
                    </motion.article>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-14 sm:mt-16 flex justify-center"
            >
                <button
                    data-testid="what-you-get-cta-button"
                    onClick={() => scrollToId("checkout")}
                    className="group relative w-full sm:w-auto overflow-hidden border border-orchid/60 text-white font-mono text-xs sm:text-sm tracking-[0.2em] uppercase px-12 py-5 hover:bg-orchid hover:border-orchid transition-colors duration-300"
                >
                    Start for {PRICE}
                </button>
            </motion.div>
        </div>
    </section>
);
